import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu as MenuIcon } from '@mui/icons-material';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import GlobalStyles from '@mui/material/GlobalStyles';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { useScrollPosition } from '@n8tb1t/use-scroll-position';
import { SrOnly, Visible } from '@skyslope/mache';
import { twMerge } from 'tailwind-merge';

import SkyslopeFormsImg from 'assets/images/ss-apps/SkySlope.svg';
import SkyslopeDefaultImg from 'assets/images/ss-apps/SkySlopeLogoDefault.svg';
import useIsLoginProcessComplete from 'auth/hooks/useIsLoginProcessComplete';
import { FormsLogoLink } from 'components/Common/FormsLogoLink';
import { TrialBanner } from 'components/TrialBanner/TrialBanner';
import { onboarding, type RoutePath, routes } from 'constants/routes';
import { useCanApproveBuyerAgreements } from 'hooks/domain/Roles/queries/useCanApproveBuyerAgreements';
import { useHasLeaderboardPermission } from 'hooks/domain/Roles/useHasLeaderboardPermission';
import { useHeaderVariant } from 'hooks/integrators/useHeaderVariant';
import { useThemeQueryParam } from 'hooks/integrators/useThemeQueryParam';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import type { Paths } from 'store/types';
import { isAppRunningInIframe } from 'utils/isAppRunningInIframe';

import { AppHeaderLinkNames } from '../AppHeader';
import MobileMenuMainContent from '../MobileMenu/components/MobileMenuMainContent';
import FormsTabs from '../Tabs/FormsTabs';

import BrowseLibrariesNavButton from './BrowseLibrariesNavButton';
import MainMenuAppDropdown from './MainMenuAppDropdown';
import MainMenuBreadcrumbs from './MainMenuBreadcrumbs';
import MainMenuHelpDropdown from './MainMenuHelpDropdown';
import MainMenuUserDropdown from './MainMenuUserDropdown';
import NavMenuItem from './NavMenuItem';

// UX wants the header left menu to line up vertically with the page content
// so manually calculated the max width and set the container margin to auto
const HEADER_MENU_MAX_WIDTH = '1477px';
export interface MainMenuProps {
  title?: string;
  tabs: Paths;
  smallHeader?: boolean;
  breadcrumbs?: { label: string; path: string }[];
}

const debounceWaitMs = 300;
const minScrollToOpenClose = 80;
const openIfAbove = 5;

const MainMenu: React.FC<MainMenuProps> = ({ title, tabs = [], smallHeader = false, breadcrumbs }: MainMenuProps) => {
  const theme = useTheme();
  const { useDefaultHeader } = useHeaderVariant();
  const themeQueryParam = useThemeQueryParam();
  const [isHideOnScroll, setIsHideOnScroll] = React.useState(true);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = React.useState(false);
  const { pathname } = useLocation();
  const isLoginProcessComplete = useIsLoginProcessComplete();
  const isOnboarding = !isLoginProcessComplete || onboarding.includes(pathname as RoutePath);
  const isInIframe = isAppRunningInIframe();
  const canApproveBuyerAgreements = useCanApproveBuyerAgreements().data;
  const hasLeaderboardPermission = useHasLeaderboardPermission().data;
  const flags = useFeatureFlags();

  const shouldHideOnScroll = (): boolean => {
    if (smallHeader) {
      return false;
    }
    return isHideOnScroll;
  };

  useScrollPosition(
    ({ prevPos, currPos }) => {
      let isShow = isHideOnScroll;
      if (currPos.y < openIfAbove) {
        isShow = true;
      } else if (currPos.y > prevPos.y + minScrollToOpenClose) {
        isShow = false;
      } else if (currPos.y < prevPos.y - minScrollToOpenClose) {
        isShow = true;
      }
      if (isShow !== isHideOnScroll) setIsHideOnScroll(isShow);
    },
    [isHideOnScroll],
    undefined,
    true,
    debounceWaitMs
  );

  if (themeQueryParam === 'agnostic') {
    return (
      <AppBar elevation={4} color="inherit" position="sticky" id="desktop-main-menu-app-bar">
        <Toolbar
          sx={{
            paddingLeft: '0px !important',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '78px !important',
          }}
        >
          <img alt="SkySlope" src={SkyslopeDefaultImg} width={146} height={30} />
        </Toolbar>
      </AppBar>
    );
  }

  return (
    <>
      <GlobalStyles
        styles={{
          body: {
            // NOTE: in the long run both of these can be removed for ResizeObserver
            '--header-height': theme.spacing(14),
            '--header-height-with-fallback': theme.spacing(24),
          },
          a: {
            textDecoration: 'none',
          },
        }}
      />
      <AppBar elevation={4} color="inherit" position="sticky" id="desktop-main-menu-app-bar">
        <TrialBanner />
        <Toolbar
          className="mui-lg:!pl-0"
          sx={{
            paddingTop: theme.spacing(3),
            display: 'flex',
            alignItems: 'flex-start',
            minHeight: '78px !important',
          }}
        >
          <Visible above="lg">
            <div className={twMerge('relative w-[148px] ml-8', shouldHideOnScroll() ? '[&>a]:-top-3' : '[&>a]:top-0')}>
              <Link to={routes.homeTownBuffet} id="files-dashboard-link">
                <SrOnly>Go to files dashboard</SrOnly>
                <img alt="" src={SkyslopeFormsImg} width={120} height={30} />
              </Link>
            </div>
          </Visible>
          <Box
            component="div"
            sx={{
              paddingRight: theme.spacing(4),
              width: '100%',
              minWidth: 0,
              maxWidth: HEADER_MENU_MAX_WIDTH,
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            <Collapse in={shouldHideOnScroll()}>
              <div
                className={twMerge(
                  'flex justify-between flex-grow max-xl:pr-0 h-6 pr-8 ml-2 mb-10',
                  !useDefaultHeader && 'h-0 mb-0'
                )}
              >
                <Visible below="lg">
                  <div className="flex items-center">
                    {useDefaultHeader && (
                      <IconButton onClick={() => setIsMobileDrawerOpen(true)} className="mr-4">
                        <MenuIcon />
                      </IconButton>
                    )}
                    <FormsLogoLink />
                  </div>
                </Visible>

                {useDefaultHeader && (
                  <>
                    <Visible above="lg" className="w-full">
                      <Box component="div" sx={{ height: theme.spacing(4) }} className="w-full">
                        <NavMenuItem
                          name={AppHeaderLinkNames.Files}
                          path={routes.homeTownBuffet}
                          width={34}
                          isActive={/dashboard|file-details/.test(pathname)}
                          id="Files-Header-Nav-Button"
                        />
                        <NavMenuItem
                          name={AppHeaderLinkNames.Templates}
                          path={routes.templateManagement}
                          width={76}
                          id="Templates-Header-Nav-Button"
                        />
                        {!isOnboarding && <BrowseLibrariesNavButton />}
                        {flags.isMyClausesEnabled && (
                          <NavMenuItem
                            name={AppHeaderLinkNames.Clauses}
                            path={routes.clauses}
                            width={76}
                            id="Clauses-Header-Nav-Button"
                          />
                        )}
                        {canApproveBuyerAgreements && (
                          <NavMenuItem
                            name={AppHeaderLinkNames.BuyerAgreements}
                            path={routes.buyerAgreements}
                            width={136}
                            id="BuyerAgreements-Header-Nav-Button"
                          />
                        )}
                        {hasLeaderboardPermission && (
                          <NavMenuItem
                            name={AppHeaderLinkNames.Leaderboard}
                            path={routes.leaderboard}
                            width={94}
                            id="Leaderboard-Header-Nav-Button"
                          />
                        )}
                      </Box>
                    </Visible>

                    <Box
                      component="div"
                      sx={{
                        height: theme.spacing(4),
                        marginTop: theme.spacing(-0.75),
                        '& > button': {
                          marginTop: theme.spacing(-0.125),
                          padding: theme.spacing(0.75, 1),
                        },
                      }}
                      className="flex items-center shrink-0"
                    >
                      {!isOnboarding && (
                        <>
                          <MainMenuHelpDropdown />
                          {!isInIframe && <MainMenuAppDropdown />}
                          <MainMenuUserDropdown />
                        </>
                      )}
                    </Box>
                  </>
                )}
              </div>
            </Collapse>
            {smallHeader && <MainMenuBreadcrumbs breadcrumbs={breadcrumbs} />}
            {!tabs.length && (
              <Box
                component="div"
                sx={
                  smallHeader
                    ? {
                        paddingLeft: theme.spacing(4),
                        paddingBottom: theme.spacing(3),
                      }
                    : {
                        paddingLeft: theme.spacing(4),
                        paddingBottom: theme.spacing(4),
                      }
                }
              >
                {title === '' ? (
                  <Skeleton animation="wave" width={300} height={32} />
                ) : (
                  <Typography
                    sx={
                      smallHeader
                        ? {
                            fontSize: theme.spacing(3),
                            lineHeight: theme.spacing(4), // bug in material-ui
                          }
                        : undefined
                    }
                    variant="h3"
                    component="h1"
                    noWrap
                  >
                    {title}
                  </Typography>
                )}
              </Box>
            )}
            {!!tabs.length && (
              <>
                <div className={twMerge('pb-6 pl-8', !useDefaultHeader && 'max-mui-lg:pl-36')}>
                  {title === '' ? (
                    <Skeleton animation="wave" width={300} height={40} />
                  ) : (
                    <Typography variant="h3" component="h1" noWrap>
                      {title}
                    </Typography>
                  )}
                </div>
                <FormsTabs paths={tabs} />
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      <Visible below="lg">
        <SwipeableDrawer
          id="sidebar-drawer"
          anchor="left"
          open={isMobileDrawerOpen}
          onOpen={() => setIsMobileDrawerOpen(true)}
          onClose={() => setIsMobileDrawerOpen(false)}
          classes={{ paper: 'w-full max-w-[320px]' }}
        >
          <MobileMenuMainContent handleDrawerClose={() => setIsMobileDrawerOpen(false)} />
        </SwipeableDrawer>
      </Visible>
    </>
  );
};

export default MainMenu;
